import { Menu, MenuButton, IconButton, MenuList, MenuItem, Box, useDisclosure, } from '@chakra-ui/react';
import React from 'react';
import KebabMenuIcon from '../../../icons/KebabIcon/KebabIcon';
import { useTokens } from '../../../../context/TokenProvider';
import { CLOSED } from '../../../../constants/statuses';
import { RenderIf } from '../../../modules/RenderIf/RenderIf';
import UpdateInspectionStatusModal from '../../../forms/InspectionStatus/upgrade/UpdateInspectionStatusModal';
import EditVisitDetailsModal from '../../../forms/EditVisitDetails/upgrade/EditVisitDetailsModal';
import ShareInspectionModal from '../../../forms/ShareInspection/upgrade/ShareInspectionModal';
import { EditPropertyDetailsModalWrapper } from './EditPropertyDetailsModalWrapper';
import useExportInspectionUseCase from '../../../../hooks/usecases/inspections/useExportInspectionUseCase';
import { localise } from '../../../../services/lang/lang';
import { useManyDisclosureProxy } from '../../../../hooks/state/useManyDisclosureProxy';
import { getGetAllContactsQueryKey, getGetInspectionScoringQueryKey, getGetScoringValidationStatusQueryKey, } from '../../../../services/api/generated/client';
import { useInspectionDetailContext } from './InspectionDetailProvider';
import { queryClient } from '../../../../services/api/query/queryClient';
import AddContactModal from '../../../modules/Contacts/Modal/AddContactModal';
import { UnsavedChangesProvider } from '../../../modules/UnsavedChanges/UnsavedChangesProvider';
import { ModalErrorProvider } from '../../../modules/Modal/ModalErrorProvider';
const InspectionDetailActionMenu = ({ inspection, isViewOnly, canEditInspection, }) => {
    const buttonStyle = { w: '56px', h: '48px' };
    const { onInspectionMutated } = useInspectionDetailContext();
    const { buildOnOpen, isOpen, buildOnClose, onClose } = useManyDisclosureProxy({
        changeStatusModal: useDisclosure(),
        editVisitDetailsModal: useDisclosure(),
        shareExternalModal: useDisclosure(),
        editPropertyModal: useDisclosure(),
        addEditContactModal: useDisclosure(),
    });
    const exportInspectionUseCase = useExportInspectionUseCase(inspection.id);
    const tokens = useTokens();
    const canEditStatus = !isViewOnly && canEditInspection;
    const canEditDetails = inspection.status.id !== CLOSED && !isViewOnly && canEditInspection;
    const actions = [
        {
            label: 'Edit visit details',
            condition: canEditDetails,
            testId: 'inspection_detail_action_menu__edit_visit',
            onClick: buildOnOpen('editVisitDetailsModal'),
        },
        {
            label: 'Edit property details',
            condition: canEditDetails,
            testId: 'inspection_detail_action_menu__edit_property',
            onClick: buildOnOpen('editPropertyModal'),
        },
        {
            label: 'Change status',
            condition: canEditStatus,
            testId: 'inspection_detail_action_menu__change_status',
            onClick: buildOnOpen('changeStatusModal'),
        },
        {
            label: 'Add contact',
            condition: canEditDetails,
            testId: 'inspection_detail_action_menu__add_contact',
            onClick: buildOnOpen('addEditContactModal'),
        },
        {
            label: 'Share externally',
            condition: !isViewOnly,
            testId: 'inspection_detail_action_menu__share_externally',
            onClick: buildOnOpen('shareExternalModal'),
        },
        {
            label: 'Export',
            condition: !isViewOnly,
            testId: 'inspection_detail_action_menu__export',
            onClick: exportInspectionUseCase.execute,
        },
    ];
    const isLoading = exportInspectionUseCase.isFetching;
    return (React.createElement(Box, null,
        React.createElement(Menu, null,
            React.createElement(MenuButton, { isLoading: isLoading, sx: buttonStyle, onClick: (e) => e.stopPropagation(), as: IconButton, variant: "white-grey", "data-testid": "inspection_detail_action_menu__kebab_button", colorScheme: tokens.Layer01, icon: React.createElement(KebabMenuIcon, null), "aria-label": localise('INSPECTION_ACTIONS') }),
            React.createElement(MenuList, null, actions.map((action, index) => (React.createElement(RenderIf, { key: `inspection_detail_action_${index}`, condition: action.condition },
                React.createElement(MenuItem, { onClick: 'onClick' in action ? action.onClick : undefined, "data-testid": action.testId }, action.label)))))),
        React.createElement(RenderIf, { condition: isOpen('changeStatusModal') },
            React.createElement(UpdateInspectionStatusModal, { isOpen: true, inspection: inspection, onClose: buildOnClose('changeStatusModal'), onSuccess: () => {
                    onClose('changeStatusModal');
                    onInspectionMutated?.(localise('INSPECTION_STATUS'));
                } })),
        React.createElement(RenderIf, { condition: isOpen('editVisitDetailsModal') },
            React.createElement(UnsavedChangesProvider, null,
                React.createElement(ModalErrorProvider, null,
                    React.createElement(EditVisitDetailsModal, { inspection: inspection, onClose: buildOnClose('editVisitDetailsModal'), onSuccess: () => {
                            onClose('editVisitDetailsModal');
                            onInspectionMutated?.(localise('VISIT_DETAILS'));
                        } })))),
        React.createElement(RenderIf, { condition: isOpen('shareExternalModal') },
            React.createElement(ShareInspectionModal, { inspection: inspection, onClose: buildOnClose('shareExternalModal') })),
        React.createElement(RenderIf, { condition: isOpen('editPropertyModal') },
            React.createElement(EditPropertyDetailsModalWrapper, { inspection: inspection, onClose: buildOnClose('editPropertyModal'), onSuccess: () => {
                    onClose('editPropertyModal');
                    onInspectionMutated?.(localise('PROPERTY_DETAILS'));
                    queryClient.invalidateQueries({
                        queryKey: getGetInspectionScoringQueryKey(inspection.id),
                    });
                    queryClient.invalidateQueries({
                        queryKey: getGetScoringValidationStatusQueryKey(inspection.id),
                    });
                } })),
        React.createElement(RenderIf, { condition: isOpen('addEditContactModal') },
            React.createElement(UnsavedChangesProvider, null,
                React.createElement(AddContactModal, { isOpen: true, onClose: buildOnClose('addEditContactModal'), inspectionId: inspection.id, onAddContact: () => {
                        onClose('addEditContactModal');
                        queryClient.invalidateQueries({
                            queryKey: getGetAllContactsQueryKey(inspection.id),
                        });
                    } })))));
};
export default InspectionDetailActionMenu;
